"use client";

import { motion } from "framer-motion";
import { Warehouse, Factory, Store, ThermometerSun, Sparkles, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const facilities = [
  {
    icon: Warehouse,
    image: "/images/hangar-conservation.png",
    title: "Hangars Conditionnés",
    subtitle: "Conservation & Stockage",
    description:
      "Nos hangars à atmosphère contrôlée préservent la fraîcheur des racines tubercules pendant des semaines. Température, hygrométrie et ventilation sont pilotées électroniquement pour stopper la germination et conserver la valeur nutritionnelle.",
    features: [
      { icon: ThermometerSun, label: "Température 13–15°C" },
      { icon: Sparkles, label: "Hygrométrie 85–90%" },
      { icon: Warehouse, label: "Capacité 500 tonnes" },
    ],
    badge: "Climate Control",
  },
  {
    icon: Factory,
    image: "/images/usine-transformation.png",
    title: "Usine de Transformation",
    subtitle: "Agroalimentaire industrielle",
    description:
      "Notre unité de transformation industrielle traite quotidiennement des tonnes de manioc, patates et ignames. Lavage, râpage, fermentation, séchage, cuisson, conditionnement : toutes les étapes sont automatisées et certifiées HACCP.",
    features: [
      { icon: Factory, label: "5 tonnes / jour" },
      { icon: Sparkles, label: "Norme HACCP" },
      { icon: Warehouse, label: "12 lignes de production" },
    ],
    badge: "Certifié HACCP",
  },
  {
    icon: Store,
    image: "/images/boutique-exposition.png",
    title: "Boutiques d'Exposition",
    subtitle: "Vente directe & dégustation",
    description:
      "Des espaces élégants où le public découvre, touche, sent et goûte. Racines fraîches, farines, chips, attiéké, plats préparés : chaque boutique est une vitrine du savoir-faire PATATE OR et un point de contact direct avec nos clients.",
    features: [
      { icon: Store, label: "04 boutiques ouvertes" },
      { icon: MapPin, label: "Centres-villes & marchés" },
      { icon: Sparkles, label: "Dégustation gratuite" },
    ],
    badge: "Vente directe",
  },
];

export function Infrastructure() {
  return (
    <section id="infrastructure" className="relative py-24 lg:py-32 bg-gradient-to-b from-background to-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Infrastructure
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            De la <span className="text-gradient-gold">racine</span> à la{" "}
            <span className="text-gradient-gold">boutique</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Trois maillons d&apos;une chaîne maîtrisée, tous implantés à{" "}
            <span className="font-semibold text-gradient-gold">Ndiamsyl Peye, Smartest Capital</span>{" "}
            — notre berceau agricole. PATATE OR investit dans des infrastructures modernes
            pour garantir la qualité, du champ africain jusqu&apos;à l&apos;assiette du
            consommateur.
          </motion.p>
        </div>

        {/* Facility cards */}
        <div className="mt-16 grid lg:grid-cols-3 gap-6">
          {facilities.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="group relative overflow-hidden rounded-3xl bg-card border border-border hover:border-gold hover:shadow-gold-lg transition-all duration-500"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={f.image}
                  alt={f.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/95 via-earth-deep/30 to-transparent" />

                <div className="absolute top-4 right-4">
                  <Badge className="bg-gradient-gold text-white shadow-gold border-0">
                    {f.badge}
                  </Badge>
                </div>

                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-gold shadow-gold">
                      <f.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-gold-soft font-semibold">
                        {f.subtitle}
                      </div>
                      <h3 className="font-display text-xl font-bold text-cream">
                        {f.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-muted-foreground leading-relaxed min-h-[100px]">
                  {f.description}
                </p>

                <ul className="mt-4 grid grid-cols-1 gap-2">
                  {f.features.map((feat, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-foreground/75">
                      <feat.icon className="w-3.5 h-3.5 text-gold-deep" />
                      <span className="font-medium">{feat.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 rounded-2xl glass-card"
        >
          {[
            { value: "500 t", label: "Capacité de stockage" },
            { value: "5 t/j", label: "Capacité de transformation" },
            { value: "04", label: "Boutiques en activité" },
            { value: "HACCP", label: "Certification alimentaire" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-gradient-gold">
                {s.value}
              </div>
              <div className="mt-1 text-[11px] sm:text-xs uppercase tracking-wider text-muted-foreground">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
